import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Typography, Card, CardContent, IconButton, Button, Menu, MenuItem, ListItemIcon, useMediaQuery, Link, TableContainer, Table, TableCell, TableRow, TableHead, TableBody, Tooltip } from '@mui/material';
import { Edit, Download, MoreVert, ContentCopy, Block, MoneyOff, Send } from '@mui/icons-material';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import { Loading } from '../../../components/loading/Loading';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { CloudFunctions } from '../../../utils/enums/CloudFunctions';
import { Link as RouterLink } from 'react-router-dom';
import { invoiceRefundSnapshotListener, invoiceSnapshotListener } from '../../../repository/InvoicesDAO';
import { useUserContext } from '../../../context/user_context/UserContext';
import { organizationProductSnapshotListener } from '../../../repository/ProductsDAO';
import { ToastType, useToast } from '../../../context/toast/ToastContext';
import InvoiceStatusCell from './components/InvoiceStatusCell';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import ConfirmActionModal from '../../../components/modals/ConfirmActionModal';

//TODO: PERMS

const InvoiceDetail = () => {
    const { invoiceId } = useParams();
    const { firebaseUser } = useUserContext();
    const [invoice, setInvoice] = useState();
    const [products, setProducts] = useState();
    const [productsLoaded, setProductsLoaded] = useState(false);
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmRefundOpen, setConfirmRefundOpen] = useState(false);
    const [confirmSendOpen, setConfirmSendOpen] = useState(false);
    const { setLoadingData } = useLoadingContext();
    const { organizationStripeInfo, getCustomerByID } = useOrganizationContext();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [anchorEl, setAnchorEl] = useState(null);
    const [copied, setCopied] = useState(false);
    const [refunds, setRefunds] = useState([]);

    const handleCopy = (invoiceLink) => {
        //TODO: This requires a browser permission, which is dumb. is there a better way?
        navigator.clipboard.writeText(invoiceLink)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => console.error('Failed to copy: ', err));
    };

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribe = organizationProductSnapshotListener(firebaseUser.organization, (productsData) => {
                setProducts(productsData);
                setProductsLoaded(true);
            });
            return () => unsubscribe();
        }
    }, [firebaseUser]);

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribe = invoiceSnapshotListener(firebaseUser.organization, invoiceId, setInvoice);
            return () => unsubscribe();
        }
    }, [firebaseUser, invoiceId]);

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribe = invoiceRefundSnapshotListener(firebaseUser.organization, invoiceId, setRefunds);
            return () => unsubscribe();
        }
    }, [firebaseUser, invoiceId]);

    const getProductById = (productId) => {
        const product = products.find(product => product.id === productId);
        return product ? product : null;
    };

    if (!invoice || !productsLoaded) return <AppSidebar content={<Loading />} />;

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDeleteInvoice = async () => {
        setLoadingData(true);
        try {
            const functions = getFunctions();
            const voidInvoice = httpsCallable(functions, CloudFunctions.VoidStripeInvoice);
            const result = await voidInvoice({
                invoiceId: invoice.id,
                accountId: organizationStripeInfo.stripe_account_id,
            });
    
            if (result.data.success) {
                showToast(t(StringKeys.SUCCESSFULLY_VOIDED_X, { x: t(StringKeys.INVOICE) }), ToastType.SUCCESS);
                navigate('/invoices');
            } else {
                showToast(t(StringKeys.FAILED_TO_VOID_X, { x: t(StringKeys.INVOICE) }), ToastType.DANGER);
            }
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_VOID_X, { x: t(StringKeys.INVOICE) }), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };
    

    const handleRefundInvoice = async () => {
        setLoadingData(true);
        try {
            const functions = getFunctions();
            const refundPayment = httpsCallable(functions, CloudFunctions.RefundStripeInvoice);
            
            const result = await refundPayment({
                chargeId: invoice.charge,
                organizationId: firebaseUser.organization,
                paymentIntentId: invoice.payment_intent,
                accountId: organizationStripeInfo.stripe_account_id,
            });
    
            if (result.data.success) {
                showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                console.error('Error refunding payment:', result);
            }
        } catch (error) {
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            console.error('Error refunding payment:', error);
        } finally {
            setLoadingData(false);
            setConfirmRefundOpen(false);
        }
    };

    const handleSendInvoice = async () => {
        setLoadingData(true);
        try {
            const functions = getFunctions();
            const sendInvoice = httpsCallable(functions, CloudFunctions.SendInvoiceToCustomer);
            const result = await sendInvoice({
                accountId: organizationStripeInfo.stripe_account_id,
                invoiceId: invoice.id,
            });

            if (result.data.success) {
                showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                console.error('Error sending invoice:', result.data.error);
            }
        } catch (error) {
            console.error('Error sending invoice:', error);
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        } finally {
            setLoadingData(false);
            setConfirmSendOpen(false);
        }
    };

    const handleDownloadPdf = async (invoice) => {
        try {
            if (invoice.invoice_pdf) {
                window.open(invoice.invoice_pdf, '_blank');
            } else {
                console.error('Error fetching PDF download link.');
            }
        } catch (error) {
            console.error('Error fetching PDF download link');
        }
    };

    return (
        <AppSidebar
            content={
                <>
                    <Grid container alignItems="center" spacing={0}>
                        <Grid item>
                            {!isMobile && (
                                <Grid item>
                                    <Button
                                        variant="text"
                                        startIcon={<Download />}
                                        onClick={() => handleDownloadPdf(invoice)}
                                        sx={{ textTransform: 'none', fontSize: '1rem' }}
                                    >
                                        {t(StringKeys.DOWNLOAD_PDF)}
                                    </Button>
                                    {
                                        invoice.status !== "void" && invoice.status !== "paid" && (
                                        <Button
                                            variant="text"
                                            startIcon={<Block />}
                                            onClick={() => setConfirmOpen(true)}
                                            sx={{ textTransform: 'none', fontSize: '1rem' }}
                                        >
                                            {t(StringKeys.VOID)}
                                        </Button>
                                    )}
                                    {
                                        invoice.status === "paid" && !invoice.refunded && (
                                            <Button
                                                variant="text"
                                                startIcon={<MoneyOff />}
                                                onClick={() => setConfirmRefundOpen(true)}
                                                sx={{ textTransform: 'none', fontSize: '1rem' }}
                                            >
                                                {t(StringKeys.REFUND)}
                                            </Button>
                                        )
                                    }
                                    {
                                        invoice.status !== "paid" && (
                                            <Button
                                                variant="text"
                                                startIcon={<Send />}
                                                onClick={() => setConfirmSendOpen(true)}
                                                sx={{ textTransform: 'none', fontSize: '1rem' }}
                                            >
                                                {t(StringKeys.SEND_INVOICE)}
                                            </Button>
                                        )
                                    }
                                </Grid>
                            )}
                        </Grid>

                        {isMobile && (
                            <Grid item>
                                <IconButton onClick={handleMenuClick}>
                                    <MoreVert />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem onClick={() => { handleMenuClose(); }}>
                                        <ListItemIcon>
                                            <Edit fontSize="small" />
                                        </ListItemIcon>
                                        {t(StringKeys.EDIT)}
                                    </MenuItem>
                                    <MenuItem onClick={() => { 
                                        handleDownloadPdf(invoice); 
                                        handleMenuClose(); 
                                    }}>
                                        <ListItemIcon>
                                            <Download fontSize="small" />
                                        </ListItemIcon>
                                        {t(StringKeys.DOWNLOAD_PDF)}
                                    </MenuItem>
                                        {
                                            invoice.status !== "void" && invoice.status !== "paid" && (
                                                <MenuItem onClick={() => { setConfirmOpen(true); handleMenuClose(); }}>
                                                    <ListItemIcon>
                                                        <Block fontSize="small" />
                                                    </ListItemIcon>
                                                    {t(StringKeys.VOID)}
                                                </MenuItem>                                               
                                            )
                                        }
                                        {
                                            invoice.status === "paid" && (
                                                <MenuItem onClick={() => { setConfirmRefundOpen(true); handleMenuClose(); }}>
                                                    <ListItemIcon>
                                                        <MoneyOff fontSize="small" />
                                                    </ListItemIcon>
                                                    {t(StringKeys.REFUND)}
                                                </MenuItem>                                                
                                            )
                                        }
                                </Menu>
                            </Grid>
                        )}
                    </Grid>

                    <Card variant="outlined" sx={{ mt: 3 }}>
                        <CardContent>
                        <Typography variant={isMobile ? "h6" : "h5"}>{t(StringKeys.INVOICE)} #{invoice.number}</Typography>
                            <Typography>
                            {`${t(StringKeys.CUSTOMER)}: `}
                                <Link 
                                    component={RouterLink} 
                                    to={`/customers/${invoice.customer_id}`}
                                >
                                    {getCustomerByID(invoice.customer_id).first_name} {getCustomerByID(invoice.customer_id).last_name}
                                </Link>
                            </Typography>
                            <Typography>{t(StringKeys.AMOUNT_DUE)}: ${(invoice.total / 100).toFixed(2)} {invoice.currency.toUpperCase()}</Typography>
                            <Typography>{t(StringKeys.STATUS)}: <InvoiceStatusCell refunded={invoice.refunded} status={invoice.status} t={t} /></Typography>
                            <Typography>{t(StringKeys.CREATED_DATE)}: {new Date(invoice.created_at.seconds * 1000).toLocaleDateString()}</Typography>
                            <Typography>{t(StringKeys.DUE_DATE)}: {new Date(invoice.due_date.seconds * 1000).toLocaleDateString()}</Typography>
                            <Typography>
                                <Tooltip title={t(StringKeys.COPIED)}open={copied} onClose={() => setCopied(false)} leaveDelay={1000}>
                                    <Link
                                        onClick={() => {
                                            handleCopy(invoice.hosted_invoice_url);
                                            setCopied(true);
                                        }}
                                        sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                                    >
                                        {t(StringKeys.INVOICE_PAYMENT_LINK)} <ContentCopy sx={{ ml: 1 }} />
                                    </Link>
                                </Tooltip>
                            </Typography>

                            {invoice.paidAt && (
                                <Typography>{t(StringKeys.PAID_DATE)}: {new Date(invoice.paid_at.seconds * 1000).toLocaleDateString()} - {new Date(invoice.paidAt.seconds * 1000).toLocaleTimeString()}</Typography>
                            )}
                        </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ mt: 3 }}>
                        <CardContent>
                            <Typography variant={isMobile ? "h6" : "h5"} component="div">{t(StringKeys.LINE_ITEMS)}</Typography>
                            <TableContainer>
                                <Table sx={{minWidth:750}}>

                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t(StringKeys.ITEM)}</TableCell>
                                            <TableCell>{t(StringKeys.DESCRIPTION)}</TableCell>
                                            <TableCell>{t(StringKeys.UNIT_PRICE)}</TableCell>
                                            <TableCell>{t(StringKeys.QUANTITY)}</TableCell>
                                            <TableCell>{t(StringKeys.TOTAL_PRICE)}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoice.items && invoice.items.length > 0 ? (
                                            invoice.items.map((lineItem, index) => {
                                                const product = getProductById(lineItem.price.product);
                                                return (
                                                    <TableRow key={index}>
                                                        <TableCell>{index + 1}</TableCell> {/* Item Number */}
                                                        <TableCell>{product ? product.name : t(StringKeys.UNAVAILABLE)}</TableCell> {/* Product Description */}
                                                        <TableCell>${(lineItem.unit_amount_excluding_tax / 100).toFixed(2)}</TableCell> {/* Unit Price */}
                                                        <TableCell>{lineItem.quantity}</TableCell> {/* Quantity */}
                                                        <TableCell>${(lineItem.amount / 100).toFixed(2)}</TableCell> {/* Total Price */}
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography>{t(StringKeys.NO_ITEMS)}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    {refunds.length > 0 && (
                        <Card variant="outlined" sx={{ mt: 3 }}>
                            <CardContent>
                                <Typography variant={isMobile ? "h6" : "h5"} component="div">{t(StringKeys.REFUND_DETAILS)}</Typography>
                                <TableContainer>
                                    <Table sx={{minWidth:750}}>

                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t(StringKeys.AMOUNT_REFUNDED)}</TableCell>
                                                <TableCell>{t(StringKeys.STATUS)}</TableCell>
                                                <TableCell>{t(StringKeys.CREATED_DATE)}</TableCell>
                                                <TableCell>{t(StringKeys.RECEIPT_URL)}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {refunds.map((refund, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>${(refund.amount_refunded / 100).toFixed(2)} {refund.currency.toUpperCase()}</TableCell>
                                                    <TableCell>{t(refund.status)}</TableCell>
                                                    <TableCell>{new Date(refund.created_at.seconds * 1000).toLocaleString()}</TableCell>
                                                    <TableCell>
                                                        {refund.receipt_url ? (
                                                            <Link href={refund.receipt_url} target="_blank" rel="noopener noreferrer">
                                                                {t(StringKeys.VIEW_RECEIPT)}
                                                            </Link>
                                                        ) : (
                                                            t(StringKeys.NOT_AVAILABLE)
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}


                    <ConfirmDeleteModal
                        open={confirmOpen}
                        onClose={() => setConfirmOpen(false)}
                        onConfirm={handleDeleteInvoice}
                        item={t(StringKeys.THIS_INVOICE)}
                    />

                    <ConfirmActionModal
                        open={confirmRefundOpen}
                        onClose={() => setConfirmRefundOpen(false)}
                        onConfirm={handleRefundInvoice}
                        title={t(StringKeys.CONFIRM_REFUND)}
                        message={t(StringKeys.ARE_YOU_SURE_YOU_WANT_TO_REFUND_THIS_INVOICE)}
                    />

                    <ConfirmActionModal
                        open={confirmSendOpen}
                        onClose={() => setConfirmSendOpen(false)}
                        onConfirm={handleSendInvoice}
                        title={t(StringKeys.CONFIRM_SEND)}
                        message={t(StringKeys.ARE_YOU_SURE_YOU_WANT_TO_SEND_THIS_INVOICE)}
                    />
                </>
            }
        />
    );
};

export default InvoiceDetail;
